import { useState, useRef, useCallback, useEffect } from 'react'

export type RecorderState = 'idle' | 'recording' | 'paused' | 'stopped'

interface AudioRecorderResult {
  state: RecorderState
  elapsedSeconds: number
  audioLevel: number
  /** Finalized audio segments. May be more than one if the mic stream was interrupted mid-recording. */
  audioSegments: Blob[] | null
  /** True if recording was interrupted and recovered at least once (segments will be stitched server-side). */
  wasInterrupted: boolean
  error: string | null
  start: () => Promise<void>
  stop: () => void
  pause: () => void
  resume: () => void
  reset: () => void
}

function getSupportedMimeType(): string {
  const types = [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/mp4',
    'audio/ogg;codecs=opus',
  ]
  for (const type of types) {
    if (MediaRecorder.isTypeSupported(type)) return type
  }
  return ''
}

// A single MediaRecorder lifecycle produces one segment. Segments from different
// MediaRecorder instances cannot be naively concatenated (each has its own container
// init segment), so we keep them separate and stitch server-side with ffmpeg.
const MAX_CHUNKS_PER_SEGMENT_BEFORE_CONSOLIDATE = 30
// If no audio chunks arrive for this long while we believe we're recording, assume
// the mic stream was silently suspended (iOS backgrounding, etc.) and force recovery.
const CHUNK_WATCHDOG_STALL_MS = 15_000
const CHUNK_WATCHDOG_INTERVAL_MS = 5_000
// Give up after this many consecutive failed recovery attempts — preserves whatever
// segments we already have instead of looping forever.
const MAX_CONSECUTIVE_RECOVERY_FAILURES = 3

export function useAudioRecorder(): AudioRecorderResult {
  const [state, setState] = useState<RecorderState>('idle')
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  const [audioLevel, setAudioLevel] = useState(0)
  const [audioSegments, setAudioSegments] = useState<Blob[] | null>(null)
  const [wasInterrupted, setWasInterrupted] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const mediaRecorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  // Finalized segments — one per MediaRecorder lifecycle
  const segmentsRef = useRef<Blob[]>([])
  // Chunks for the in-progress segment (gets consolidated and eventually turned into a Blob)
  const currentChunksRef = useRef<Blob[]>([])
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)
  const audioCtxRef = useRef<AudioContext | null>(null)
  const animFrameRef = useRef<number | null>(null)
  const startTimeRef = useRef<number>(0)
  const pausedElapsedRef = useRef<number>(0)
  const wakeLockRef = useRef<WakeLockSentinel | null>(null)
  const mimeTypeRef = useRef<string>('')
  const recoveringRef = useRef(false)
  const recoveryFailuresRef = useRef(0)
  const lastChunkAtRef = useRef<number>(0)
  const watchdogRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const stateRef = useRef<RecorderState>('idle')
  // Broken circular dependency between recoverStream and the watchdog
  const recoverStreamRef = useRef<() => Promise<void>>(async () => {})

  // Keep stateRef in sync so event listeners can read current state
  stateRef.current = state

  const acquireWakeLock = useCallback(async () => {
    if ('wakeLock' in navigator) {
      try {
        wakeLockRef.current = await navigator.wakeLock.request('screen')
      } catch {
        // Wake lock request can fail (e.g. low battery) — recording still works
      }
    }
  }, [])

  const releaseWakeLock = useCallback(() => {
    if (wakeLockRef.current) {
      wakeLockRef.current.release().catch(() => {})
      wakeLockRef.current = null
    }
  }, [])

  const stopLevelMonitoring = useCallback(() => {
    if (animFrameRef.current !== null) {
      clearInterval(animFrameRef.current as unknown as number)
      animFrameRef.current = null
    }
    setAudioLevel(0)
  }, [])

  const startLevelMonitoring = useCallback(() => {
    const analyser = analyserRef.current
    if (!analyser) return

    const dataArray = new Uint8Array(analyser.frequencyBinCount)

    // Sample at ~10fps instead of 60fps to avoid killing iOS with re-renders
    animFrameRef.current = setInterval(() => {
      analyser.getByteFrequencyData(dataArray)
      let sum = 0
      for (let i = 0; i < dataArray.length; i++) {
        sum += dataArray[i]!
      }
      const avg = sum / dataArray.length / 255
      setAudioLevel(avg)
    }, 100) as unknown as number
  }, [])

  const stopTimer = useCallback(() => {
    if (timerRef.current !== null) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startTimer = useCallback(() => {
    stopTimer()
    startTimeRef.current = Date.now()
    timerRef.current = setInterval(() => {
      const now = Date.now()
      const elapsed = pausedElapsedRef.current + Math.floor((now - startTimeRef.current) / 1000)
      setElapsedSeconds(elapsed)
    }, 1000)
  }, [stopTimer])

  const stopWatchdog = useCallback(() => {
    if (watchdogRef.current !== null) {
      clearInterval(watchdogRef.current)
      watchdogRef.current = null
    }
  }, [])

  const startWatchdog = useCallback(() => {
    stopWatchdog()
    lastChunkAtRef.current = Date.now()
    watchdogRef.current = setInterval(() => {
      if (stateRef.current !== 'recording') return
      const silence = Date.now() - lastChunkAtRef.current
      if (silence > CHUNK_WATCHDOG_STALL_MS) {
        // No chunks for >15s despite being in "recording" state — the underlying
        // MediaRecorder is likely silently suspended. Force recovery.
        recoverStreamRef.current()
      }
    }, CHUNK_WATCHDOG_INTERVAL_MS)
  }, [stopWatchdog])

  const releaseStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop())
      streamRef.current = null
    }
    if (audioCtxRef.current) {
      audioCtxRef.current.close().catch(() => {})
      audioCtxRef.current = null
    }
    analyserRef.current = null
  }, [])

  // Finalize the currently-recording segment into a Blob and push it onto segmentsRef.
  // Called whenever a MediaRecorder lifecycle ends — either stop(), recoverStream(), or reset().
  const finalizeCurrentSegment = useCallback(() => {
    if (currentChunksRef.current.length === 0) return
    const blob = new Blob(currentChunksRef.current, { type: mimeTypeRef.current })
    if (blob.size > 0) {
      segmentsRef.current.push(blob)
    }
    currentChunksRef.current = []
  }, [])

  // Wire up the handlers for a freshly-created MediaRecorder. Each recorder appends
  // to currentChunksRef; on stop, the segment is finalized (either as the final one
  // during stop() or as an intermediate one during recoverStream()).
  const attachRecorderHandlers = useCallback((recorder: MediaRecorder, isRecovery: boolean) => {
    const mimeType = mimeTypeRef.current

    recorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        lastChunkAtRef.current = Date.now()
        // A chunk arrived — the recovery (if any) was successful, reset the failure counter.
        recoveryFailuresRef.current = 0
        currentChunksRef.current.push(e.data)
        if (currentChunksRef.current.length >= MAX_CHUNKS_PER_SEGMENT_BEFORE_CONSOLIDATE) {
          currentChunksRef.current = [new Blob(currentChunksRef.current, { type: mimeType })]
        }
      }
    }

    recorder.onstop = () => {
      // Final stop — finalize whatever's in currentChunksRef and expose all segments.
      finalizeCurrentSegment()
      setAudioSegments(segmentsRef.current.length > 0 ? [...segmentsRef.current] : null)
      setState('stopped')
      stopTimer()
      stopLevelMonitoring()
      stopWatchdog()
    }

    recorder.onerror = () => {
      setError('recordingFailed')
      setState('idle')
      stopTimer()
      stopLevelMonitoring()
      stopWatchdog()
      releaseStream()
    }

    if (isRecovery) {
      setWasInterrupted(true)
    }
  }, [finalizeCurrentSegment, stopTimer, stopLevelMonitoring, stopWatchdog, releaseStream])

  // Recover from an interrupted mic stream (iOS system dialog, backgrounding, device
  // change, etc.). Finalizes the current segment, kills the dead recorder, and starts
  // a fresh MediaRecorder that appends to a NEW segment. Segments are stitched
  // server-side by ffmpeg on upload.
  const recoverStream = useCallback(async () => {
    if (recoveringRef.current) return
    if (stateRef.current !== 'recording') return
    recoveringRef.current = true

    try {
      const mimeType = mimeTypeRef.current
      if (!mimeType) return

      recoveryFailuresRef.current += 1
      if (recoveryFailuresRef.current > MAX_CONSECUTIVE_RECOVERY_FAILURES) {
        // Too many failed recoveries — stop gracefully with whatever segments we have.
        finalizeCurrentSegment()
        if (segmentsRef.current.length > 0) {
          setAudioSegments([...segmentsRef.current])
          setState('stopped')
        } else {
          setError('recordingFailed')
          setState('idle')
        }
        stopTimer()
        stopLevelMonitoring()
        stopWatchdog()
        releaseStream()
        releaseWakeLock()
        return
      }

      // Finalize the current (interrupted) segment first — its chunks are captured.
      finalizeCurrentSegment()

      // Kill the dead recorder gracefully — detach handlers so onstop doesn't fire.
      const oldRecorder = mediaRecorderRef.current
      if (oldRecorder) {
        oldRecorder.onstop = null
        oldRecorder.onerror = null
        oldRecorder.ondataavailable = null
        if (oldRecorder.state !== 'inactive') {
          try { oldRecorder.stop() } catch { /* already gone */ }
        }
      }

      // Release old stream + audio context
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
        streamRef.current = null
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {})
        audioCtxRef.current = null
      }
      analyserRef.current = null
      stopLevelMonitoring()

      // Request fresh mic access
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Rebuild audio level monitoring
      const audioCtx = new AudioContext()
      audioCtxRef.current = audioCtx
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser

      // Create new recorder — chunks start accumulating in a fresh currentChunksRef
      // (already cleared by finalizeCurrentSegment above).
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32_000 })
      mediaRecorderRef.current = recorder
      attachRecorderHandlers(recorder, true)

      // Listen for the new track dying too
      stream.getAudioTracks().forEach(track => {
        track.addEventListener('ended', () => {
          if (mediaRecorderRef.current === recorder && stateRef.current === 'recording') {
            recoverStreamRef.current()
          }
        })
      })

      recorder.start(10_000)
      // Reset the watchdog clock so we don't immediately re-trigger before first chunk.
      lastChunkAtRef.current = Date.now()
      startLevelMonitoring()
      acquireWakeLock()
      setWasInterrupted(true)
    } catch {
      // This recovery attempt failed. We don't give up yet — the next track.ended,
      // visibilitychange, or watchdog tick will retry. If we hit the consecutive
      // failure limit, the early-return branch at the top of the next call will
      // gracefully stop with what we have.
    } finally {
      recoveringRef.current = false
    }
  }, [
    finalizeCurrentSegment,
    stopTimer,
    startLevelMonitoring,
    stopLevelMonitoring,
    stopWatchdog,
    releaseStream,
    releaseWakeLock,
    acquireWakeLock,
    attachRecorderHandlers,
  ])

  // Keep the ref in sync so the watchdog and track.ended listeners can call the
  // latest version without being part of their dependency arrays.
  recoverStreamRef.current = recoverStream

  const start = useCallback(async () => {
    setError(null)
    setAudioSegments(null)
    setWasInterrupted(false)
    segmentsRef.current = []
    currentChunksRef.current = []
    pausedElapsedRef.current = 0
    recoveryFailuresRef.current = 0
    setElapsedSeconds(0)

    if (typeof MediaRecorder === 'undefined') {
      setError('notSupported')
      return
    }

    const mimeType = getSupportedMimeType()
    if (!mimeType) {
      setError('notSupported')
      return
    }
    mimeTypeRef.current = mimeType

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      streamRef.current = stream

      // Set up audio level monitoring
      const audioCtx = new AudioContext()
      audioCtxRef.current = audioCtx
      const source = audioCtx.createMediaStreamSource(stream)
      const analyser = audioCtx.createAnalyser()
      analyser.fftSize = 256
      source.connect(analyser)
      analyserRef.current = analyser

      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 32_000 })
      mediaRecorderRef.current = recorder
      attachRecorderHandlers(recorder, false)

      // Auto-recover if iOS kills the mic (system dialog, incoming call, etc.)
      stream.getAudioTracks().forEach(track => {
        track.addEventListener('ended', () => {
          if (mediaRecorderRef.current === recorder && stateRef.current === 'recording') {
            recoverStreamRef.current()
          }
        })
      })

      // Request data every 10s to reduce chunk count on long recordings
      recorder.start(10_000)
      setState('recording')
      startTimer()
      startLevelMonitoring()
      startWatchdog()
      acquireWakeLock()
    } catch (err) {
      if (err instanceof DOMException && (err.name === 'NotAllowedError' || err.name === 'PermissionDeniedError')) {
        setError('microphoneAccess')
      } else {
        setError('recordingFailed')
      }
      releaseStream()
    }
  }, [
    startTimer,
    startLevelMonitoring,
    startWatchdog,
    releaseStream,
    acquireWakeLock,
    attachRecorderHandlers,
  ])

  const stop = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && (recorder.state === 'recording' || recorder.state === 'paused')) {
      recorder.stop() // triggers onstop which finalizes segments, sets state + blob
    } else {
      // Recorder is already inactive (e.g. died during recovery) — finalize manually.
      finalizeCurrentSegment()
      setAudioSegments(segmentsRef.current.length > 0 ? [...segmentsRef.current] : null)
      setState('stopped')
      stopTimer()
      stopLevelMonitoring()
      stopWatchdog()
    }
    releaseStream()
    releaseWakeLock()
  }, [finalizeCurrentSegment, stopTimer, stopLevelMonitoring, stopWatchdog, releaseStream, releaseWakeLock])

  const pause = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state === 'recording') {
      recorder.pause()
      pausedElapsedRef.current += Math.floor((Date.now() - startTimeRef.current) / 1000)
      stopTimer()
      stopLevelMonitoring()
      stopWatchdog()
      releaseWakeLock()
      setState('paused')
    }
  }, [stopTimer, stopLevelMonitoring, stopWatchdog, releaseWakeLock])

  const resume = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state === 'paused') {
      recorder.resume()
      startTimer()
      startLevelMonitoring()
      startWatchdog()
      acquireWakeLock()
      setState('recording')
    }
  }, [startTimer, startLevelMonitoring, startWatchdog, acquireWakeLock])

  const reset = useCallback(() => {
    const recorder = mediaRecorderRef.current
    if (recorder && recorder.state !== 'inactive') {
      // Stop without saving — clear chunks first
      currentChunksRef.current = []
      segmentsRef.current = []
      recorder.onstop = null
      recorder.ondataavailable = null
      try { recorder.stop() } catch { /* already gone */ }
    }
    releaseStream()
    releaseWakeLock()
    stopTimer()
    stopLevelMonitoring()
    stopWatchdog()
    mediaRecorderRef.current = null
    segmentsRef.current = []
    currentChunksRef.current = []
    pausedElapsedRef.current = 0
    recoveryFailuresRef.current = 0
    setElapsedSeconds(0)
    setAudioSegments(null)
    setWasInterrupted(false)
    setError(null)
    setState('idle')
  }, [releaseStream, releaseWakeLock, stopTimer, stopLevelMonitoring, stopWatchdog])

  // Re-acquire wake lock and recover dead streams when returning to app
  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState !== 'visible') return
      if (state !== 'recording') return

      const recorder = mediaRecorderRef.current
      const track = streamRef.current?.getAudioTracks()[0]

      // If the recorder or track died while backgrounded (iOS system dialog, etc.), recover
      if (!recorder || recorder.state === 'inactive' || track?.readyState === 'ended') {
        recoverStreamRef.current()
        return
      }

      acquireWakeLock()
      // iOS can suspend AudioContext when backgrounded — resume it
      if (audioCtxRef.current?.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {})
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange)
    return () => document.removeEventListener('visibilitychange', onVisibilityChange)
  }, [state, acquireWakeLock])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      const recorder = mediaRecorderRef.current
      if (recorder && recorder.state !== 'inactive') {
        recorder.onstop = null
        recorder.stop()
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop())
      }
      if (audioCtxRef.current) {
        audioCtxRef.current.close().catch(() => {})
      }
      if (timerRef.current !== null) clearInterval(timerRef.current)
      if (animFrameRef.current !== null) clearInterval(animFrameRef.current as unknown as number)
      if (watchdogRef.current !== null) clearInterval(watchdogRef.current)
      if (wakeLockRef.current) wakeLockRef.current.release().catch(() => {})
    }
  }, [])

  return { state, elapsedSeconds, audioLevel, audioSegments, wasInterrupted, error, start, stop, pause, resume, reset }
}
